import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  ChevronDown, ChevronUp, Calendar, CheckCircle, Clock,
  History, MessageSquare, DollarSign, IndianRupee, Paperclip, Lock, AlertCircle,
} from 'lucide-react';
import Select from 'react-select';
import { useParams } from 'react-router-dom';

// ─── Constants ───────────────────────────────────────────────────────────────
const CURRENT_USER_UID  = window.localStorage.getItem('projectUserUID')  || 'unknown';
const CURRENT_USER_NAME = window.localStorage.getItem('projectUserDisplayName') || 'Unknown';
const CURRENT_USER_ROLE = window.localStorage.getItem('projectUserRole') || 'Unknown';

const API_URL = 'https://demo-api-384317551782.europe-west1.run.app';
// const API_URL = 'http://localhost:3001';

// ─── Firebase Storage ────────────────────────────────────────────────────────
const uploadToFirebase = async (file, leadId, folderName, index = null) => {
  const storage = getStorage();
  const ext  = file.name.substring(file.name.lastIndexOf('.'));
  const path = index !== null
    ? `${leadId}/${folderName}/${folderName}_${index}${ext}`
    : `${leadId}/${folderName}/${folderName}${ext}`;
  const snap = await uploadBytes(ref(storage, path), file);
  return getDownloadURL(snap.ref);
};

const uploadMultipleFiles = async (fileArray, leadId, folderName) => {
  if (!fileArray || fileArray.length === 0) return [];
  return Promise.all(fileArray.map((f, i) => uploadToFirebase(f, leadId, folderName, i)));
};

const uploadTaskAttachments = async (attachments, leadId, taskId) => {
  const storage = getStorage();
  const upload  = (files, folder) =>
    files.map((file, i) => {
      const ext  = file.name.substring(file.name.lastIndexOf('.'));
      const path = `${leadId}/task-updates/${taskId}/${folder}/attachment_${i}${ext}`;
      return uploadBytes(ref(storage, path), file).then((s) => getDownloadURL(s.ref));
    });

  const [momUrls, siteUrls, quoteUrls] = await Promise.all([
    Promise.all(upload(attachments.mom        || [], 'mom')),
    Promise.all(upload(attachments.siteImages || [], 'site-images')),
    Promise.all(upload(attachments.quote      || [], 'quote')),
  ]);
  return { mom: momUrls, siteImages: siteUrls, quote: quoteUrls };
};

// ─── REST API ────────────────────────────────────────────────────────────────
const api = {
  fetchTasks: async (leadId) => {
    const res = await fetch(`${API_URL}/api/lead/${leadId}/tasks`);
    if (!res.ok) throw new Error('Failed to fetch tasks');
    return res.json();
  },

  addTask: async (leadId, payload) => {
    const res = await fetch(`${API_URL}/api/lead/${leadId}/tasks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to add task');
    return res.json();
  },

  startTask: async (leadId, taskId, startedBy, startedByName) => {
    const res = await fetch(`${API_URL}/api/tasks/${leadId}/${taskId}/start`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ startedBy, startedByName }),
    });
    if (!res.ok) throw new Error('Failed to start task');
    return res.json();
  },

  addUpdate: async (leadId, taskId, payload) => {
    const res = await fetch(`${API_URL}/api/tasks/${leadId}/${taskId}/updates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to add task update');
    return res.json();
  },

  markWon: async (leadId, payload) => {
    const res = await fetch(`${API_URL}/api/leads/${leadId}/won`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to mark lead as won');
    return res.json();
  },

  markLost: async (leadId, payload) => {
    const res = await fetch(`${API_URL}/api/lead/${leadId}/lost`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to mark lead as lost');
    return res.json();
  },
};

// ─── React Query ─────────────────────────────────────────────────────────────
const TASK_KEYS = { all: (id) => ['leadTasks', id] };

export const useLeadTasks = (leadId) =>
  useQuery({
    queryKey:  TASK_KEYS.all(leadId),
    queryFn:   () => api.fetchTasks(leadId),
    select:    (res) => res.data,
    enabled:   !!leadId,
    staleTime: 30_000,
  });

export const useAddTask = (leadId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.addTask(leadId, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: TASK_KEYS.all(leadId) }),
  });
};

export const useStartTask = (leadId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId }) => api.startTask(leadId, taskId, CURRENT_USER_UID, CURRENT_USER_NAME),
    onSuccess:  () => qc.invalidateQueries({ queryKey: TASK_KEYS.all(leadId) }),
  });
};

export const useAddTaskUpdate = (leadId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskId,
      updateText,
      dealSize,
      tentativeClosureDate,
      tag,
      attachments,
      nextTask,
    }) => {
      const uploadedAttachments = await uploadTaskAttachments(attachments || {}, leadId, taskId);
      return api.addUpdate(leadId, taskId, {
        updateText,
        dealSize:             dealSize             || null,
        tentativeClosureDate: tentativeClosureDate || null,
        tag:                  tag                  || null,
        attachments:          uploadedAttachments,
        nextTask:             nextTask             || null,
        completeCurrentTask:  !!(nextTask?.name),
        updatedBy:            CURRENT_USER_UID,
        updatedByName:        CURRENT_USER_NAME,
      });
    },

    onMutate: async ({ taskId, nextTask }) => {
      await qc.cancelQueries({ queryKey: TASK_KEYS.all(leadId) });
      const previous = qc.getQueryData(TASK_KEYS.all(leadId));

      qc.setQueryData(TASK_KEYS.all(leadId), (old) => {
        if (!old?.tasks) return old;

        const ts         = new Date().toISOString();
        const doComplete = !!nextTask?.name;

        const updatedTasks = old.tasks.map((t) => {
          if (t.taskId !== taskId) return t;
          return {
            ...t,
            ...(doComplete ? { completed: true, completedAt: ts } : {}),
          };
        });

        if (doComplete && nextTask?.name) {
          updatedTasks.push({
            taskId:           `optimistic_${Date.now()}`,
            name:             nextTask.name,
            phase:            'sales',
            type:             'custom',
            start:            ts,
            completed:        false,
            completedAt:      null,
            nextFollowUpDate: nextTask.followUpDate || null,
            updates:          [],
            createdAt:        ts,
            updatedAt:        ts,
          });
        }

        return { ...old, tasks: updatedTasks };
      });

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(TASK_KEYS.all(leadId), context.previous);
      }
    },

    onSettled: () => qc.invalidateQueries({ queryKey: TASK_KEYS.all(leadId) }),
  });
};

export const useMarkWon = (leadId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData) => {
      const [txnImageUrls, salesBOQUrls, floorPlanUrls] = await Promise.all([
        uploadMultipleFiles(formData.txnImages,        leadId, 'finance-docs'),
        uploadMultipleFiles(formData.salesBOQ,         leadId, 'quotes'),
        uploadMultipleFiles(formData.floorPlan || [],  leadId, 'site-images'),
      ]);
      return api.markWon(leadId, {
        bookingAmount:   parseFloat(formData.bookingAmount),
        // ── NEW: closeAmount (pre-GST final amount) ──────────────────
        closeAmount:     formData.closeAmount ? parseFloat(formData.closeAmount) : null,
        closeAmountWithGST:     formData.closeAmountWithGST ? parseFloat(formData.closeAmountWithGST) : null,
        // ─────────────────────────────────────────────────────────────
        discount:        formData.discount  ? parseFloat(formData.discount)  : null,
        gst:             formData.gst       ? parseFloat(formData.gst)       : null,
        increment:       formData.increment ? parseFloat(formData.increment) : null,
        txnId:           formData.txnId      || null,
        paymentModes:    formData.paymentModes,
        designStartDate: formData.designStartDate,
        closureDate:     formData.closureDate,
        description:     formData.description || null,
        txnImages:       txnImageUrls,
        salesBOQFile:    salesBOQUrls,
        floorPlan:       floorPlanUrls,
        convertedBy:     CURRENT_USER_UID,
        convertedByName: CURRENT_USER_NAME,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TASK_KEYS.all(leadId) }),
  });
};

export const useMarkLost = (leadId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lostReason) =>
      api.markLost(leadId, { lostReason, markedBy: CURRENT_USER_UID, markedByName: CURRENT_USER_NAME }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TASK_KEYS.all(leadId) }),
  });
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt   = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('en-US',    { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;

const getOverdueDays = (isoDate) => {
  if (!isoDate) return null;
  const diff = Math.floor((Date.now() - new Date(isoDate).getTime()) / 86_400_000);
  return diff > 0 ? diff : null;
};

const ALLOWED_EXTS  = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const validateFiles = (files) => {
  for (const file of files) {
    if (file.size > MAX_FILE_SIZE)
      return `${file.name} is too large. Max 5 MB.`;
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!ALLOWED_EXTS.includes(ext))
      return `Invalid type for ${file.name}. Allowed: ${ALLOWED_EXTS.join(', ')}`;
  }
  return null;
};

const TAG_OPTIONS = [
  { value: 'active-warm', label: 'Active - Warm' },
  { value: 'active-cold', label: 'Active - Cold' },
  { value: 'reshedule',   label: 'Reshedule'      },
  { value: 'rnr',         label: 'RNR'            },
  { value: 'on-hold',     label: 'On Hold'        },
];

const NEXT_TASK_OPTIONS = [
  'VM - First Quote Discussion',
  'VM - Quote Negotiation',
  'VM - General Discussion',
  'Quote Comparison',
  'EC - Walkthrough',
  'EC - Quote Discussion',
  'EC - Finalization',
  'Site - Client Site Visit',
  'Site - Completed Site Tour',
  'Site - Factory Visit',
  'Payment Pending',
];

const paymentModeOptions = [
  { value: 'CreditCard',   label: 'Credit Card'   },
  { value: 'DebitCard',    label: 'Debit Card'     },
  { value: 'Cash',         label: 'Cash'           },
  { value: 'OnlineLink',   label: 'Online Link'    },
  { value: 'UPI',          label: 'UPI'            },
  { value: 'BankTransfer', label: 'Bank Transfer'  },
  { value: 'Cheque',       label: 'Cheque'         },
];

const customSelectStyles = {
  control: (p, s) => ({
    ...p, minHeight: '40px', borderRadius: '4px',
    borderColor: s.isFocused ? '#3b82f6' : '#ccc',
    boxShadow: s.isFocused ? '0 0 0 3px rgba(59,130,246,0.1)' : 'none',
    '&:hover': { borderColor: '#3b82f6' },
  }),
  multiValue:       (p) => ({ ...p, backgroundColor: '#3b82f6', borderRadius: '4px' }),
  multiValueLabel:  (p) => ({ ...p, color: 'white', fontSize: '14px' }),
  multiValueRemove: (p) => ({ ...p, color: 'white', '&:hover': { backgroundColor: '#2563eb', color: 'white' } }),
  placeholder:      (p) => ({ ...p, color: '#9ca3af', fontSize: '14px' }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isSelected ? '#3b82f6' : s.isFocused ? '#eff6ff' : 'white',
    color: s.isSelected ? 'white' : '#374151',
    padding: '8px 12px', fontSize: '14px',
  }),
};

// ─── Divider label ───────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <div className="d-flex align-items-center gap-2 mb-2 mt-3">
    <div style={{ height: '1px', flex: 1, background: '#dee2e6' }} />
    <span className="text-muted fw-semibold text-uppercase"
      style={{ fontSize: '0.68rem', letterSpacing: '0.05em' }}>{children}</span>
    <div style={{ height: '1px', flex: 1, background: '#dee2e6' }} />
  </div>
);

// ─── Folder file input ───────────────────────────────────────────────────────
const FileFolderInput = ({ label, folderKey, files, onChange, error }) => (
  <div>
    <label className="form-label small mb-1">
      <Paperclip size={12} className="me-1" />{label}
    </label>
    <input
      type="file" multiple
      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
      onChange={(e) => {
        const selected = Array.from(e.target.files);
        onChange(folderKey, selected, validateFiles(selected) || null);
      }}
      className={`form-control form-control-sm ${error ? 'is-invalid' : ''}`}
    />
    {files.length > 0 && (
      <div className="form-text text-success">{files.length} file{files.length > 1 ? 's' : ''} selected</div>
    )}
    {error && <div className="invalid-feedback d-block">{error}</div>}
    <div className="form-text" style={{ fontSize: '0.7rem' }}>jpg, png, pdf, doc · max 5 MB</div>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// UPDATE HISTORY PANEL
// ══════════════════════════════════════════════════════════════════════════════
const UpdateHistoryPanel = ({ updates }) => {
  const [isOpen, setIsOpen] = useState(true);

  if (!updates || updates.length === 0) {
    return (
      <div className="mt-3 pt-3 border-top">
        <div className="d-flex align-items-center gap-2 text-muted mb-2">
          <History size={15} /><span className="fw-semibold small">Update History</span>
        </div>
        <p className="text-muted small fst-italic mb-0">No updates yet.</p>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-top">
      <button
        className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2 text-dark mb-2"
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
      >
        <History size={15} className="text-secondary" />
        <span className="fw-semibold small">Update History</span>
        <span className="badge bg-secondary rounded-pill" style={{ fontSize: '0.7rem' }}>{updates.length}</span>
        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {isOpen && (
        <div className="position-relative ps-3" style={{ borderLeft: '2px solid #dee2e6' }}>
          {[...updates].reverse().map((update, i) => (
            <div key={i} className="mb-3 position-relative" onClick={(e) => e.stopPropagation()}>
              <div className="position-absolute bg-secondary rounded-circle border border-white"
                style={{ width: '10px', height: '10px', left: '-16px', top: '5px' }} />
              <div className="card card-body p-2 bg-light border-0" style={{ fontSize: '0.85rem' }}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                    <Clock size={11} className="me-1" />{fmtDT(update.date)}
                  </span>
                  <span className="badge bg-primary bg-opacity-10 text-primary" style={{ fontSize: '0.7rem' }}>
                    Update #{updates.length - i}
                  </span>
                </div>
                <div className="d-flex align-items-start gap-1 mb-1">
                  <MessageSquare size={13} className="text-primary mt-1 flex-shrink-0" />
                  <p className="mb-0 text-dark">{update.text}</p>
                </div>
                <div className="d-flex flex-wrap gap-2 mt-1">
                  {update.tag && (
                    <span className="badge fw-normal" style={{
                      fontSize: '0.72rem',
                      backgroundColor:
                        update.tag === 'active-warm' ? '#fd7e14' :
                        update.tag === 'active-cold' ? '#0dcaf0' :
                        update.tag === 'reshedule'   ? '#ff2a44' :
                        update.tag === 'rnr'         ? '#6c757d' : '#ffc107',
                      color: ['active-cold', 'on-hold'].includes(update.tag) ? '#000' : '#fff',
                    }}>
                      {TAG_OPTIONS.find((o) => o.value === update.tag)?.label || update.tag}
                    </span>
                  )}
                  {update.followUpDate && (
                    <span className="badge bg-info text-dark fw-normal d-flex align-items-center gap-1" style={{ fontSize: '0.72rem' }}>
                      <Calendar size={11} />Follow-up: {fmtDT(update.followUpDate)}
                    </span>
                  )}
                  {update.dealSize && (
                    <span className="badge bg-success bg-opacity-75 fw-normal d-flex align-items-center gap-1" style={{ fontSize: '0.72rem' }}>
                      <IndianRupee size={11} />Deal: ₹{parseFloat(update.dealSize).toLocaleString('en-IN')}
                    </span>
                  )}
                  {update.tentativeClosureDate && (
                    <span className="badge bg-warning text-dark fw-normal d-flex align-items-center gap-1" style={{ fontSize: '0.72rem' }}>
                      <Calendar size={11} />Closure: {fmt(update.tentativeClosureDate)}
                    </span>
                  )}
                  {update.nextTask && (
                    <span className="badge bg-secondary fw-normal" style={{ fontSize: '0.72rem' }}>
                      → Next: {update.nextTask.name}
                    </span>
                  )}
                  {update.attachments?.mom?.length        > 0 && <span className="badge bg-secondary bg-opacity-75 fw-normal" style={{ fontSize: '0.72rem' }}><Paperclip size={11} className="me-1" />MOM: {update.attachments.mom.length}</span>}
                  {update.attachments?.siteImages?.length > 0 && <span className="badge bg-secondary bg-opacity-75 fw-normal" style={{ fontSize: '0.72rem' }}><Paperclip size={11} className="me-1" />Site: {update.attachments.siteImages.length}</span>}
                  {update.attachments?.quote?.length      > 0 && <span className="badge bg-secondary bg-opacity-75 fw-normal" style={{ fontSize: '0.72rem' }}><Paperclip size={11} className="me-1" />Quote: {update.attachments.quote.length}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// UPDATE MODAL
// ══════════════════════════════════════════════════════════════════════════════
const UpdateModal = ({ isOpen, onClose, onSubmit, task, isLoading }) => {
  const [updateText,           setUpdateText]           = useState('');
  const [dealSize,             setDealSize]             = useState('');
  const [tentativeClosureDate, setTentativeClosureDate] = useState('');
  const [tag,                  setTag]                  = useState('');
  const [nextTaskName,         setNextTaskName]         = useState('');
  const [nextTaskDateTime,     setNextTaskDateTime]     = useState('');
  const [attachments,          setAttachments]          = useState({ mom: [], siteImages: [], quote: [] });
  const [fileErrors,           setFileErrors]           = useState({ mom: null, siteImages: null, quote: null });

  const handleFolderChange = (key, files, err) => {
    setAttachments((p) => ({ ...p, [key]: files }));
    setFileErrors((p)  => ({ ...p, [key]: err   }));
  };

  const reset = () => {
    setUpdateText(''); setDealSize(''); setTag(''); setTentativeClosureDate('');
    setNextTaskName(''); setNextTaskDateTime('');
    setAttachments({ mom: [], siteImages: [], quote: [] });
    setFileErrors({ mom: null, siteImages: null, quote: null });
  };

  const hasFileError = Object.values(fileErrors).some(Boolean);

  const handleSubmit = () => {
    if (!updateText.trim()) return;
    onSubmit({
      updateText,
      dealSize,
      tentativeClosureDate,
      tag,
      attachments,
      nextTask: nextTaskName
        ? { name: nextTaskName, followUpDate: nextTaskDateTime || null }
        : null,
    });
    reset();
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title mb-0">Update Task</h5>
              <p className="text-muted mb-0" style={{ fontSize: '0.82rem' }}>{task?.name}</p>
            </div>
            <button type="button" className="btn-close" onClick={onClose} disabled={isLoading} />
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">Update <span className="text-danger">*</span></label>
              <textarea
                value={updateText}
                onChange={(e) => setUpdateText(e.target.value)}
                placeholder="Enter your update here..."
                rows={3}
                className="form-control"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Tag</label>
              <select value={tag} onChange={(e) => setTag(e.target.value)} className="form-select">
                <option value="">Select a tag...</option>
                {TAG_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-6">
                <label className="form-label">Deal Size</label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <input
                    type="number" value={dealSize}
                    onChange={(e) => setDealSize(e.target.value)}
                    placeholder="Enter deal size" min="0" step="0.01"
                    className="form-control"
                  />
                </div>
              </div>
              <div className="col-12 col-sm-6">
                <label className="form-label">Tentative Closure Date</label>
                <input
                  type="date" value={tentativeClosureDate}
                  onChange={(e) => setTentativeClosureDate(e.target.value)}
                  className="form-control"
                />
              </div>
            </div>
            <SectionLabel>Attachments</SectionLabel>
            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-4">
                <FileFolderInput label="MOM"              folderKey="mom"        files={attachments.mom}        onChange={handleFolderChange} error={fileErrors.mom}        />
              </div>
              <div className="col-12 col-sm-4">
                <FileFolderInput label="Site Attachments" folderKey="siteImages" files={attachments.siteImages} onChange={handleFolderChange} error={fileErrors.siteImages} />
              </div>
              <div className="col-12 col-sm-4">
                <FileFolderInput label="Quote"            folderKey="quote"      files={attachments.quote}      onChange={handleFolderChange} error={fileErrors.quote}      />
              </div>
            </div>
            <SectionLabel>Next Task</SectionLabel>
            <div className="row g-3">
              <div className="col-12 col-sm-6">
                <label className="form-label small mb-1">Select Next Task</label>
                <select
                  value={nextTaskName}
                  onChange={(e) => setNextTaskName(e.target.value)}
                  className="form-select form-select-sm"
                >
                  <option value="">Choose next task...</option>
                  {NEXT_TASK_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="col-12 col-sm-6">
                <label className="form-label small mb-1">Follow-up Date &amp; Time</label>
                <input
                  type="datetime-local"
                  value={nextTaskDateTime}
                  onChange={(e) => setNextTaskDateTime(e.target.value)}
                  className="form-control form-control-sm"
                  disabled={!nextTaskName}
                />
              </div>
            </div>
            {nextTaskName && (
              <div className="alert alert-info py-2 px-3 mt-2 mb-0 d-flex align-items-center gap-2" style={{ fontSize: '0.82rem' }}>
                <CheckCircle size={14} className="flex-shrink-0 text-info" />
                The current task will be <strong>marked as complete</strong> once you submit.
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={isLoading}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!updateText.trim() || hasFileError || isLoading}
            >
              {isLoading
                ? <><span className="spinner-border spinner-border-sm me-2" />Saving...</>
                : 'Submit Update'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// WON BOOKING MODAL  ← closeAmount field added here
// ══════════════════════════════════════════════════════════════════════════════
const WonBookingModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const empty = {
    bookingAmount:   '',
    closeAmount:     '',   // ← NEW: final amount without GST
    closeAmountWithGST:     '',   // ← NEW: final amount without GST
    discount:        '',
    gst:             '',
    increment:       '',
    txnId:           '',
    designStartDate: '',
    closureDate:     '',
    description:     '',
  };
  const [form,         setForm]         = useState(empty);
  const [paymentModes, setPaymentModes] = useState([]);
  const [errors,       setErrors]       = useState({});
  const [files,        setFiles]        = useState({ txnImages: [], salesBOQ: [], floorPlan: [] });

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    const err = validateFiles(selected);
    if (err) { setErrors((p) => ({ ...p, [e.target.name]: err })); return; }
    setFiles((p) => ({ ...p, [e.target.name]: selected }));
    if (errors[e.target.name]) setErrors((p) => ({ ...p, [e.target.name]: '' }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.bookingAmount || parseFloat(form.bookingAmount) <= 0) e.bookingAmount = 'Booking amount required and must be > 0.';
    if (!paymentModes.length)    e.paymentModes    = 'Select at least one payment mode.';
    if (!files.txnImages.length) e.txnImages       = 'Upload at least one transaction image.';
    if (!files.salesBOQ.length)  e.salesBOQ        = 'Upload the Sales BOQ.';
    if (!form.designStartDate)   e.designStartDate = 'Design start date is required.';
    if (!form.closureDate)       e.closureDate     = 'Closure date is required.';
    return e;
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit({ ...form, paymentModes: paymentModes.map((p) => p.value), ...files });
  };

  const handleClose = () => {
    setForm(empty);
    setPaymentModes([]);
    setFiles({ txnImages: [], salesBOQ: [], floorPlan: [] });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const SH = ({ children }) => (
    <div className="d-flex align-items-center gap-2 mb-3 mt-4">
      <div style={{ height: '1px', flex: 1, background: '#dee2e6' }} />
      <span className="text-muted small fw-semibold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>{children}</span>
      <div style={{ height: '1px', flex: 1, background: '#dee2e6' }} />
    </div>
  );

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">

          {/* ── Header ── */}
          <div className="modal-header bg-success bg-opacity-10 border-success border-opacity-25">
            <div>
              <h5 className="modal-title text-success fw-bold mb-0">🎉 Lead Won</h5>
              <p className="text-muted mb-0" style={{ fontSize: '0.82rem' }}>Fill in the booking details to convert this lead to a project.</p>
            </div>
            <button type="button" className="btn-close" onClick={handleClose} disabled={isLoading} />
          </div>

          <div className="modal-body">

            {/* ══ FINANCIAL DETAILS ══ */}
            <SH>Financial Details</SH>
            <div className="row g-3">

              {/* Booking Amount */}
              <div className="col-12 col-sm-6 col-md-3">
                <label className="form-label">
                  Booking Amount <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <input
                    type="number" name="bookingAmount" value={form.bookingAmount}
                    onChange={handleChange} placeholder="0.00" min="0" step="0.01"
                    className={`form-control ${errors.bookingAmount ? 'is-invalid' : ''}`}
                  />
                </div>
                {errors.bookingAmount && (
                  <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>{errors.bookingAmount}</div>
                )}
              </div>

              {/* ── NEW: Close Amount ── */}
              <div className="col-12 col-sm-6 col-md-3">
                <label className="form-label">
                  Total BOQ Value
                  <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <input
                    type="number" name="closeAmount" value={form.closeAmount}
                    onChange={handleChange} placeholder="0.00" min="0" step="0.01"
                    className="form-control"
                  />
                </div>
                <div className="form-text" style={{ fontSize: '0.72rem' }}>
                  Final amount without GST
                </div>
              </div>

              {/* ── NEW: Close Amount with GST ── */}
              <div className="col-12 col-sm-6 col-md-3">
                <label className="form-label">
                  Total BOQ Value
                  <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <input
                    type="number" name="closeAmountWithGST" value={form.closeAmountWithGST}
                    onChange={handleChange} placeholder="0.00" min="0" step="0.01"
                    className="form-control"
                  />
                </div>
                <div className="form-text" style={{ fontSize: '0.72rem' }}>
                  Final amount with GST
                </div>
              </div>

              {/* Discount */}
              <div className="col-12 col-sm-6 col-md-3">
                <label className="form-label">Discount</label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <input
                    type="number" name="discount" value={form.discount}
                    onChange={handleChange} placeholder="0.00" min="0" step="0.01"
                    className="form-control"
                  />
                </div>
              </div>

              {/* GST */}
              <div className="col-12 col-sm-6 col-md-3">
                <label className="form-label">GST</label>
                <div className="input-group">
                  <input
                    type="number" name="gst" value={form.gst}
                    onChange={handleChange} placeholder="e.g. 18" min="0" max="100" step="0.01"
                    className="form-control"
                  />
                  <span className="input-group-text">%</span>
                </div>
              </div>

              {/* Increment */}
              <div className="col-12 col-sm-6 col-md-3">
                <label className="form-label">Increment</label>
                <div className="input-group">
                  <input
                    type="number" name="increment" value={form.increment}
                    onChange={handleChange} placeholder="0.00" min="0" step="0.01"
                    className="form-control"
                  />
                  <span className="input-group-text">%</span>
                </div>
              </div>

            </div>{/* /row financial */}

            {/* ══ TRANSACTION DETAILS ══ */}
            <SH>Transaction Details</SH>
            <div className="row g-3">

              {/* Transaction ID */}
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label">Transaction ID</label>
                <input
                  type="text" name="txnId" value={form.txnId}
                  onChange={handleChange} placeholder="e.g. TXN123456789"
                  className="form-control"
                />
              </div>

              {/* Payment Modes */}
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label">Payment Modes <span className="text-danger">*</span></label>
                <Select
                  isMulti
                  options={paymentModeOptions}
                  value={paymentModes}
                  onChange={(sel) => {
                    setPaymentModes(sel || []);
                    if (errors.paymentModes) setErrors((p) => ({ ...p, paymentModes: '' }));
                  }}
                  styles={{
                    ...customSelectStyles,
                    control: (b, s) => ({
                      ...customSelectStyles.control(b, s),
                      borderColor: errors.paymentModes ? '#dc3545' : s.isFocused ? '#3b82f6' : '#ccc',
                    }),
                  }}
                  placeholder="Select payment modes..."
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  isClearable
                  isSearchable
                />
                {errors.paymentModes && (
                  <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>{errors.paymentModes}</div>
                )}
              </div>

              {/* TXN Images */}
              <div className="col-12 col-md-4">
                <label className="form-label">TXN Images <span className="text-danger">*</span></label>
                <input
                  type="file" name="txnImages"
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx" multiple
                  onChange={handleFileChange}
                  className={`form-control ${errors.txnImages ? 'is-invalid' : ''}`}
                />
                {files.txnImages.length > 0 && (
                  <div className="form-text text-success mt-1">
                    <Paperclip size={12} className="me-1" />{files.txnImages.length} file(s) selected
                  </div>
                )}
                {errors.txnImages && <div className="invalid-feedback">{errors.txnImages}</div>}
                <div className="form-text">jpg, png, pdf, doc · max 5 MB each</div>
              </div>

            </div>{/* /row transaction */}

            {/* ══ PROJECT DETAILS ══ */}
            <SH>Project Details</SH>
            <div className="row g-3">

              {/* Design Start Date */}
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label">Design Start Date <span className="text-danger">*</span></label>
                <input
                  type="date" name="designStartDate" value={form.designStartDate}
                  onChange={handleChange}
                  className={`form-control ${errors.designStartDate ? 'is-invalid' : ''}`}
                />
                {errors.designStartDate && <div className="invalid-feedback">{errors.designStartDate}</div>}
              </div>

              {/* Closure Date */}
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label">Closure Date <span className="text-danger">*</span></label>
                <input
                  type="date" name="closureDate" value={form.closureDate}
                  onChange={handleChange}
                  className={`form-control ${errors.closureDate ? 'is-invalid' : ''}`}
                />
                {errors.closureDate && <div className="invalid-feedback">{errors.closureDate}</div>}
              </div>

              {/* Sales BOQ */}
              <div className="col-12 col-md-4">
                <label className="form-label">Sales BOQ <span className="text-danger">*</span></label>
                <input
                  type="file" name="salesBOQ"
                  accept=".pdf,.doc,.docx" multiple
                  onChange={handleFileChange}
                  className={`form-control ${errors.salesBOQ ? 'is-invalid' : ''}`}
                />
                {files.salesBOQ.length > 0 && (
                  <div className="form-text text-success mt-1">
                    <Paperclip size={12} className="me-1" />{files.salesBOQ.length} file(s) selected
                  </div>
                )}
                {errors.salesBOQ && <div className="invalid-feedback">{errors.salesBOQ}</div>}
                <div className="form-text">pdf, doc, docx · max 5 MB each</div>
              </div>

              {/* Floor Plan */}
              <div className="col-12 col-md-6">
                <label className="form-label">
                  Floor Plan <span className="text-muted small">(optional)</span>
                </label>
                <input
                  type="file" name="floorPlan"
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx" multiple
                  onChange={handleFileChange}
                  className="form-control"
                />
                {files.floorPlan.length > 0 && (
                  <div className="form-text text-success mt-1">
                    <Paperclip size={12} className="me-1" />{files.floorPlan.length} file(s) selected
                  </div>
                )}
                <div className="form-text">jpg, png, pdf, doc · max 5 MB each</div>
              </div>

              {/* Description */}
              <div className="col-12 col-md-6">
                <label className="form-label">Description / Notes</label>
                <textarea
                  name="description" value={form.description}
                  onChange={handleChange}
                  placeholder="Any additional notes..."
                  rows="3"
                  className="form-control"
                />
              </div>

            </div>{/* /row project */}

            {isLoading && (
              <div className="alert alert-info mt-3 d-flex align-items-center gap-2">
                <span className="spinner-border spinner-border-sm flex-shrink-0" />
                <span>Uploading files and saving project... please wait.</span>
              </div>
            )}

          </div>{/* /modal-body */}

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={handleClose} disabled={isLoading}>Cancel</button>
            <button className="btn btn-success" onClick={handleSubmit} disabled={isLoading}>
              {isLoading
                ? <><span className="spinner-border spinner-border-sm me-2" />Converting...</>
                : <><CheckCircle size={16} className="me-2" />Convert to Project</>}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// LOST REASON MODAL
// ══════════════════════════════════════════════════════════════════════════════
const LostReasonModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [reason, setReason] = useState('');
  if (!isOpen) return null;
  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Lead Lost — Reason</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isLoading} />
          </div>
          <div className="modal-body">
            <label className="form-label">Reason for losing this lead <span className="text-danger">*</span></label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter the reason..."
              rows="4"
              className="form-control"
            />
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose} disabled={isLoading}>Cancel</button>
            <button
              className="btn btn-danger"
              onClick={() => { if (reason.trim()) { onSubmit(reason); setReason(''); } }}
              disabled={!reason.trim() || isLoading}
            >
              {isLoading ? <span className="spinner-border spinner-border-sm" /> : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const daysBetween = (isoA, isoB) => {
  if (!isoA || !isoB) return null;
  return Math.abs(Math.round((new Date(isoB) - new Date(isoA)) / 86_400_000));
};

const daysAgo = (isoDate) => {
  if (!isoDate) return null;
  const diff = Math.floor((Date.now() - new Date(isoDate).getTime()) / 86_400_000);
  if (diff === 0) return 'today';
  if (diff === 1) return '1 day ago';
  return `${diff} days ago`;
};

// ══════════════════════════════════════════════════════════════════════════════
// TASK CARD
// ══════════════════════════════════════════════════════════════════════════════
const TaskCard = ({ task, isActive, isFirstIncomplete, onStart, onUpdate, startLoading }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const overdueDays = isActive ? getOverdueDays(task.nextFollowUpDate) : null;

  const borderClass =
    task.completed ? 'border-success bg-success bg-opacity-10' :
    isActive        ? 'border-primary  bg-primary  bg-opacity-10' :
                      'border-secondary bg-light';

  const followUpToCompletedDays = task.completed
    ? daysBetween(task.nextFollowUpDate, task.completedAt)
    : null;

  const StatusBadge = () => {
    if (overdueDays) return (
      <span className="badge fw-normal d-flex align-items-center gap-1 flex-shrink-0"
        style={{ fontSize: '0.72rem', backgroundColor: '#dc3545', color: '#fff', whiteSpace: 'nowrap' }}>
        <AlertCircle size={11} />Overdue by {overdueDays} day{overdueDays > 1 ? 's' : ''}
      </span>
    );
    if (task.completed) return (
      <span className="badge bg-success fw-normal d-flex align-items-center gap-1 flex-shrink-0"
        style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
        <CheckCircle size={11} />Completed
      </span>
    );
    if (!task.start && !isFirstIncomplete) return (
      <span className="badge bg-light text-muted border fw-normal d-flex align-items-center gap-1 flex-shrink-0"
        style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
        <Lock size={11} />Waiting
      </span>
    );
    if (task.start && !task.completed) return (
      <span className="badge bg-primary bg-opacity-75 fw-normal flex-shrink-0"
        style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
        Active
      </span>
    );
    return null;
  };

  return (
    <div className={`card mb-3 border-2 ${borderClass} ${task.completed ? 'opacity-80' : ''}`}>
      <div className="card-body py-3 px-3" onClick={() => setIsExpanded(!isExpanded)} style={{ cursor: 'pointer' }}>
        <div className="d-flex justify-content-between align-items-start gap-2">
          <h6 className="mb-0 fw-semibold lh-sm" style={{ fontSize: '0.95rem' }}>
            {task.name}
          </h6>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <StatusBadge />
            {isExpanded ? <ChevronUp size={16} className="text-muted" /> : <ChevronDown size={16} className="text-muted" />}
          </div>
        </div>

        {task.completed ? (
          <>
            <div className="d-flex flex-wrap align-items-center gap-2 mt-1" style={{ fontSize: '0.8rem', color: '#6c757d' }}>
              <span className="d-flex align-items-center gap-1">
                <CheckCircle size={12} className="text-success" />
                Completed: {fmtDT(task.completedAt)}
              </span>
              {followUpToCompletedDays !== null && task.nextFollowUpDate && (
                <span className="d-flex align-items-center gap-1">
                  <span className="text-muted">·</span>
                  <Clock size={12} />
                  {followUpToCompletedDays === 0
                    ? 'Completed on follow-up day'
                    : `${followUpToCompletedDays} day${followUpToCompletedDays > 1 ? 's' : ''} from follow-up`}
                </span>
              )}
            </div>
            <div className="d-flex flex-wrap align-items-center gap-1 mt-1" style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
              {(task.createdByName || task.assigneeName) && <span>{task.createdByName || task.assigneeName}</span>}
              {task.createdAt && <><span>·</span><span>{fmtDT(task.createdAt)}</span><span>·</span><span>{daysAgo(task.createdAt)}</span></>}
            </div>
          </>
        ) : (
          <>
            {task.nextFollowUpDate && (
              <div className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                <Calendar size={12} />
                <span>{fmtDT(task.nextFollowUpDate)}</span>
              </div>
            )}
            <div className="d-flex flex-wrap align-items-center gap-1 mt-1" style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
              {(task.createdByName || task.assigneeName) && <span>{task.createdByName || task.assigneeName}</span>}
              {task.createdAt && <><span>·</span><span>{fmtDT(task.createdAt)}</span><span>·</span><span>{daysAgo(task.createdAt)}</span></>}
              {task.updates?.length > 0 && (
                <><span>·</span>
                  <span className="d-flex align-items-center gap-1">
                    <History size={11} />{task.updates.length} update{task.updates.length > 1 ? 's' : ''}
                  </span>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {isExpanded && (
        <div className="card-footer bg-white border-top" onClick={(e) => e.stopPropagation()}>
     
        {!task.completed && (
          <div className="d-flex gap-2 justify-content-end mb-2">
            {/* ↓ guard added */}
            {!['preSales', 'preSalesTeamLead'].includes(CURRENT_USER_ROLE) && (
              <>
                {isFirstIncomplete && !task.start && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onStart(task.taskId); }}
                    className="btn btn-primary btn-sm"
                    disabled={startLoading}
                  >
                    {startLoading ? <span className="spinner-border spinner-border-sm" /> : 'Start Task'}
                  </button>
                )}
                {task.start && !task.completed && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onUpdate(task.taskId); }}
                    className="btn btn-warning btn-sm"
                  >
                    Close Task
                  </button>
                )}
              </>
            )}
          </div>
        )}
          <UpdateHistoryPanel updates={task.updates || []} />
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SIDEBAR — admin only
// ══════════════════════════════════════════════════════════════════════════════
const WhatsNextSection = ({ onTaskSelect, isLoading }) => {
  const [isOpen,       setIsOpen]       = useState(false);
  const [selectedTask, setSelectedTask] = useState('');
  const [date,         setDate]         = useState('');
  const [time,         setTime]         = useState('');

  const handleAdd = () => {
    if (!selectedTask || !date) return;
    const combined = time ? `${date}T${time}` : date;
    onTaskSelect(selectedTask, combined);
    setSelectedTask(''); setDate(''); setTime(''); setIsOpen(false);
  };

  return (
    <div className="card border-warning border-2 bg-warning bg-opacity-10 mb-4">
      <div className="card-body">
        <h5 className="card-title mb-3">What's Next?</h5>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="btn btn-warning w-100 d-flex align-items-center justify-content-between"
        >
          <span>Add Task</span>
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        {isOpen && (
          <div className="mt-3">
            <div className="mb-2">
              <label className="form-label small mb-1">Select Task</label>
              <select value={selectedTask} onChange={(e) => setSelectedTask(e.target.value)} className="form-select form-select-sm">
                <option value="">Choose a task...</option>
                {NEXT_TASK_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="mb-2">
              <label className="form-label small mb-1">Follow-up Date <span className="text-danger">*</span></label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="form-control form-control-sm" />
            </div>
            <div className="mb-2">
              <label className="form-label small mb-1">Follow-up Time</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="form-control form-control-sm" disabled={!date} />
            </div>
            <button
              className="btn btn-warning btn-sm w-100"
              onClick={handleAdd}
              disabled={!selectedTask || !date || isLoading}
            >
              {isLoading ? <span className="spinner-border spinner-border-sm" /> : 'Add Task'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const LeadTasks = () => {
  const { leadId } = useParams();

  const [taskFilter,             setTaskFilter]             = useState('all');
  const [isLostReasonModalOpen,  setIsLostReasonModalOpen]  = useState(false);
  const [isUpdateModalOpen,      setIsUpdateModalOpen]      = useState(false);
  const [isWonBookingModalOpen,  setIsWonBookingModalOpen]  = useState(false);
  const [currentTaskIdForUpdate, setCurrentTaskIdForUpdate] = useState(null);

  const { data, isLoading, isError, error } = useLeadTasks(leadId);
  const tasks      = data?.tasks      || [];
  const leadStatus = data?.leadStatus || 'active';

  const addTaskMut   = useAddTask(leadId);
  const startTaskMut = useStartTask(leadId);
  const addUpdateMut = useAddTaskUpdate(leadId);
  const markWonMut   = useMarkWon(leadId);
  const markLostMut  = useMarkLost(leadId);

  const filteredTasks = useMemo(() => {
    const list = taskFilter === 'active-only'
      ? tasks.filter((t) => t.start && !t.completed)
      : tasks;
    return [...list].reverse();
  }, [taskFilter, tasks]);

  const isLeadClosed = leadStatus === 'won' || leadStatus === 'lost';

  const firstIncompleteTaskId = useMemo(() => {
    const t = tasks.find((t) => !t.completed);
    return t?.taskId || null;
  }, [tasks]);

  const currentTaskForUpdate = tasks.find((t) => t.taskId === currentTaskIdForUpdate) || null;

  const handleStart  = (taskId) => startTaskMut.mutate({ taskId });
  const handleUpdate = (taskId) => { setCurrentTaskIdForUpdate(taskId); setIsUpdateModalOpen(true); };

  const handleUpdateSubmit = (updateData) => {
    if (!currentTaskIdForUpdate) return;
    const { nextTask, ...rest } = updateData;
    addUpdateMut.mutate(
      { taskId: currentTaskIdForUpdate, ...rest, nextTask },
      {
        onSuccess: () => {
          setIsUpdateModalOpen(false);
          setCurrentTaskIdForUpdate(null);
        },
      }
    );
  };

  const handleAddTask = (taskName, followUpDate = '') => {
    addTaskMut.mutate({
      name:             taskName,
      phase:            'sales',
      type:             'custom',
      autoStart:        tasks.length > 0 && tasks.every((t) => t.completed),
      nextFollowUpDate: followUpDate || null,
      createdBy:        CURRENT_USER_UID,
      createdByName:    CURRENT_USER_NAME,
    });
  };

  if (isLoading) {
    return (
      <div className="min-vh-100 bg-light d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" />
          <p className="text-muted">Loading tasks...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-vh-100 bg-light d-flex align-items-center justify-content-center">
        <div className="alert alert-danger text-center" style={{ maxWidth: 400 }}>
          <strong>Failed to load tasks.</strong>
          <p className="mb-0 mt-1 small">{error?.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      <div className="container-fluid py-4">
        <div className="row">

          {/* ── Task list ── */}
          <div className="col-lg-8">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex gap-2">
                <button
                  onClick={() => setIsWonBookingModalOpen(true)}
                  disabled={isLeadClosed || markWonMut.isPending}
                  className="btn btn-success"
                >
                  Won
                </button>
                <button
                  onClick={() => setIsLostReasonModalOpen(true)}
                  disabled={isLeadClosed || markLostMut.isPending}
                  className="btn btn-danger"
                >
                  Lost
                </button>
              </div>
              <select
                value={taskFilter}
                onChange={(e) => setTaskFilter(e.target.value)}
                className="form-select w-auto"
              >
                <option value="all">All Tasks</option>
                <option value="active-only">Active Tasks Only</option>
              </select>
            </div>

            <h4 className="mb-3">Tasks ({filteredTasks.length})</h4>

            {filteredTasks.length > 0 ? filteredTasks.map((task) => (
              <TaskCard
                key={task.taskId}
                task={task}
                isActive={!!(task.start && !task.completed)}
                isFirstIncomplete={task.taskId === firstIncompleteTaskId}
                onStart={handleStart}
                onUpdate={handleUpdate}
                startLoading={startTaskMut.isPending && startTaskMut.variables?.taskId === task.taskId}
              />
            )) : (
              <p className="text-muted text-center py-5">No tasks yet</p>
            )}

            {isLeadClosed && (
              <div className={`alert ${leadStatus === 'won' ? 'alert-success' : 'alert-danger'} mt-4`}>
                <p className="mb-0 text-center fw-bold">
                  {leadStatus === 'won'
                    ? '🎉 This lead has been converted to a project.'
                    : '❌ This lead has been marked as LOST.'}
                </p>
                {leadStatus === 'won' && data?.bookingAmount && (
                  <p className="mb-0 text-center small mt-2">
                    <strong>Booking Amount:</strong> ₹{parseFloat(data.bookingAmount).toLocaleString('en-IN')}
                  </p>
                )}
                {leadStatus === 'won' && data?.closeAmount && (
                  <p className="mb-0 text-center small mt-1">
                    <strong>Close Amount (excl. GST):</strong> ₹{parseFloat(data.closeAmount).toLocaleString('en-IN')}
                  </p>
                )}
                {leadStatus === 'lost' && data?.lostReason && (
                  <p className="mb-0 text-center small mt-2">
                    <strong>Reason:</strong> {data.lostReason}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ── Sidebar ── */}
          <div className="col-lg-4">
            {CURRENT_USER_ROLE === 'admin' && (
              <WhatsNextSection onTaskSelect={handleAddTask} isLoading={addTaskMut.isPending} />
            )}
          </div>

        </div>
      </div>

      {/* ── Modals ── */}
      <UpdateModal
        isOpen={isUpdateModalOpen}
        onClose={() => { setIsUpdateModalOpen(false); setCurrentTaskIdForUpdate(null); }}
        onSubmit={handleUpdateSubmit}
        task={currentTaskForUpdate}
        isLoading={addUpdateMut.isPending}
      />
      <WonBookingModal
        isOpen={isWonBookingModalOpen}
        onClose={() => setIsWonBookingModalOpen(false)}
        onSubmit={(fd) => markWonMut.mutate(fd, { onSuccess: () => setIsWonBookingModalOpen(false) })}
        isLoading={markWonMut.isPending}
      />
      <LostReasonModal
        isOpen={isLostReasonModalOpen}
        onClose={() => setIsLostReasonModalOpen(false)}
        onSubmit={(r) => markLostMut.mutate(r, { onSuccess: () => setIsLostReasonModalOpen(false) })}
        isLoading={markLostMut.isPending}
      />
    </div>
  );
};

export default LeadTasks;